/**
 * Debounced pre-submit verdict for the feedback modal. Advisory: there is deliberately no
 * error state, every failure looks like "no opinion yet". Staleness is handled by
 * AbortController; the previous verdict stays on screen while the next is in flight.
 */
import { useEffect, useRef, useState } from 'react';
import { evaluateFeedback, MIN_FEEDBACK_CHARS, type VerdictResult } from './evaluate';
import type { FeedbackTool } from './types';

/** Long enough that ordinary typing pauses do not trigger a call. */
const DEBOUNCE_MS = 1200;

export interface UseVerdictInput {
  /** Only evaluates while true - no calls for a closed modal. */
  active: boolean;
  tool: FeedbackTool;
  feedback: string;
  route: string;
  csrfToken: string | null;
}

export interface UseVerdictState {
  verdict: VerdictResult | null;
  /** True only while a request is genuinely outstanding. */
  loading: boolean;
}

export function useFeedbackVerdict(input: UseVerdictInput): UseVerdictState {
  const { active, tool, feedback, route, csrfToken } = input;

  const [verdict, setVerdict] = useState<VerdictResult | null>(null);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // Everything except `feedback` is captured in a ref: the tool and route do
  // not change while a modal is open, and putting them in the dependency
  // array would re-run this effect on unrelated re-renders.
  const stableRef = useRef({ tool, route, csrfToken });
  stableRef.current = { tool, route, csrfToken };

  useEffect(() => {
    if (!active) {
      abortRef.current?.abort();
      abortRef.current = null;
      setVerdict(null);
      setLoading(false);
      return;
    }

    const trimmed = feedback.trim();
    if (trimmed.length < MIN_FEEDBACK_CHARS) {
      // Below the floor there is nothing to say, and an old verdict for
      // longer text would be actively wrong - clear it.
      abortRef.current?.abort();
      abortRef.current = null;
      setVerdict(null);
      setLoading(false);
      return;
    }

    const timer = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);

      void evaluateFeedback({
        tool: stableRef.current.tool,
        feedback: trimmed,
        route: stableRef.current.route,
        csrfToken: stableRef.current.csrfToken,
        signal: controller.signal,
      })
        .then((result) => {
          if (controller.signal.aborted) return;
          setVerdict(result);
          setLoading(false);
        })
        .catch(() => {
          // Only an abort reaches here (evaluateFeedback resolves null for
          // every other failure). A superseded request must not clear the
          // spinner belonging to the request that replaced it.
          if (!controller.signal.aborted) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // `active` and `feedback` only, deliberately - see the ref above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, feedback]);

  // Abort anything outstanding if the modal unmounts mid-flight.
  useEffect(() => () => abortRef.current?.abort(), []);

  return { verdict, loading };
}
